import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// How far (px) below the detection line an element's top may sit and still
// count as "reached the top", so a jump/scroll that lands a hair short registers
// the target, not the element above it.
const kScrollTrackTolerancePx = 24;

interface TrackedRect {
  id: string;
  top: number;
  bottom: number;
}

export function scrollTrackDetectionPoint(
  viewportTop: number,
  viewportBottom: number,
  remainingScroll?: number
): number {
  if (remainingScroll === undefined) return viewportTop;
  const viewportHeight = viewportBottom - viewportTop;
  return (
    viewportTop + Math.max(0, viewportHeight - Math.max(0, remainingScroll))
  );
}

export function selectTrackedElement(
  rects: TrackedRect[],
  elementIds: ReadonlySet<string>,
  viewportTop: number,
  viewportBottom: number,
  detectionPoint: number
): string | null {
  let crossedId: string | null = null;
  let firstVisibleId: string | null = null;
  for (const rect of rects) {
    if (!elementIds.has(rect.id)) continue;
    if (rect.bottom < viewportTop || rect.top > viewportBottom) continue;
    firstVisibleId ??= rect.id;
    if (rect.top <= detectionPoint + kScrollTrackTolerancePx) {
      crossedId = rect.id;
    }
  }
  return crossedId ?? firstVisibleId;
}

/**
 * Track which element is currently at the top of a scroll container.
 *
 * Calls `onElementVisible` when the element at the detection point (the top of
 * the viewport, just below any sticky chrome) changes. An element whose top
 * sits up to kScrollTrackTolerancePx below the line still counts as reached, so
 * a jump that lands a hair short registers the target, not the element above it.
 * In the final viewport, where rows can no longer reach the top line, the
 * detection point advances through the viewport as scrolling approaches the
 * end. This lets each tail row become current instead of jumping directly from
 * an earlier row to the last one.
 */
export function useScrollTrack(
  elementIds: string[],
  onElementVisible: (id: string) => void,
  scrollRef?: RefObject<HTMLElement | null>,
  options?: {
    topOffset?: number;
    checkInterval?: number;
    /**
     * When false, the detection point stays at the true viewport top instead
     * of advancing into the viewport as the scroll nears the end. The advance
     * (default) lets each short tail row read as "current" for a highlight
     * (the outline); a consumer that needs the row actually pinned at the top
     * (transcript j/k stepping) turns it off, so it never reports a LATER row
     * than the one at the top near the end of the log.
     */
    advanceDetectionPointAtEnd?: boolean;
  }
) {
  const currentVisibleRef = useRef<string | null>(null);
  const lastCheckRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);

  // Some consumers rebuild the ids array every render; keying the Set and
  // the subscription effect on array identity would tear down and re-register
  // the scroll listener + interval per render (starving the interval under
  // streaming re-renders). Keep the previous array while the CONTENT is equal
  // (render-phase adjust, before paint), so a stableIds identity change IS a
  // content change.
  const [stableIds, setStableIds] = useState(elementIds);
  if (
    stableIds !== elementIds &&
    (stableIds.length !== elementIds.length ||
      stableIds.some((id, i) => id !== elementIds[i]))
  ) {
    setStableIds(elementIds);
  }
  const elementIdSet = useMemo(() => new Set(stableIds), [stableIds]);

  // Latest-value ref so an inline consumer callback doesn't churn the
  // subscription, and interval/scroll fires never report through a stale
  // closure.
  const onElementVisibleRef = useRef(onElementVisible);
  useEffect(() => {
    onElementVisibleRef.current = onElementVisible;
  });

  const findTopmostVisibleElement = useCallback(() => {
    const container = scrollRef?.current;
    const containerRect = container?.getBoundingClientRect();
    const topOffset = options?.topOffset ?? 50;

    // Define viewport bounds
    const viewportTop = containerRect
      ? containerRect.top + topOffset
      : topOffset;
    const viewportBottom = containerRect
      ? containerRect.bottom
      : window.innerHeight;

    const advanceAtEnd = options?.advanceDetectionPointAtEnd ?? true;
    const remainingScroll =
      advanceAtEnd && container
        ? container.scrollHeight - container.clientHeight - container.scrollTop
        : undefined;
    const detectionPoint = scrollTrackDetectionPoint(
      viewportTop,
      viewportBottom,
      remainingScroll
    );

    const elements = container
      ? container.querySelectorAll("[id]")
      : document.querySelectorAll("[id]");
    // Measure ONLY tracked elements — rect reads force layout, and containers
    // hold many more [id] nodes (panels, messages) than tracked rows.
    const rects: TrackedRect[] = [];
    for (const element of elements) {
      if (!elementIdSet.has(element.id)) continue;
      const rect = element.getBoundingClientRect();
      rects.push({ id: element.id, top: rect.top, bottom: rect.bottom });
    }
    return selectTrackedElement(
      rects,
      elementIdSet,
      viewportTop,
      viewportBottom,
      detectionPoint
    );
  }, [
    elementIdSet,
    scrollRef,
    options?.topOffset,
    options?.advanceDetectionPointAtEnd,
  ]);

  const checkVisibility = useCallback(() => {
    const now = Date.now();
    const checkInterval = options?.checkInterval ?? 100;

    if (now - lastCheckRef.current < checkInterval) {
      return;
    }

    lastCheckRef.current = now;
    const topmostId = findTopmostVisibleElement();

    if (topmostId !== currentVisibleRef.current) {
      currentVisibleRef.current = topmostId;
      if (topmostId) {
        onElementVisibleRef.current(topmostId);
      }
    }
  }, [findTopmostVisibleElement, options?.checkInterval]);

  const handleScroll = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      checkVisibility();
      rafRef.current = null;
    });
  }, [checkVisibility]);

  // The stableIds the last report was issued against. When the tracked set
  // genuinely changes (collapse/filter), consumers that derive state from
  // the id AND the array (e.g. the transcript maps the top event to a
  // turn-anchor index) need a fresh report even if the SAME element is still
  // on top — so drop the cached current id and the throttle stamp, letting
  // the effect's synchronous check below re-report. stableIds identity IS
  // content equality (see above), so a plain identity compare suffices. A
  // single re-check, not a retry loop: fresh mounts keep their existing
  // single checkVisibility, and the scroll listener and interval remain the
  // fallback for rows that haven't mounted yet.
  const reportedIdsRef = useRef<string[] | null>(null);

  useEffect(() => {
    if (stableIds.length === 0) return;

    const scrollElement = scrollRef?.current || window;

    if (reportedIdsRef.current !== stableIds) {
      reportedIdsRef.current = stableIds;
      currentVisibleRef.current = null;
      lastCheckRef.current = 0;
    }
    checkVisibility();

    scrollElement.addEventListener("scroll", handleScroll, { passive: true });

    const intervalId = setInterval(checkVisibility, 1000);

    return () => {
      scrollElement.removeEventListener("scroll", handleScroll);
      clearInterval(intervalId);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [stableIds, scrollRef, handleScroll, checkVisibility]);
}
